import type { AccountType, TrialBalance, TrialBalanceRow } from '@/api/models';

/**
 * Wire ⇄ domain mapping for the trial balance (bos-api).
 *
 * **This is the only file that knows the server's field names.** Everything
 * downstream — the service, the query hook, the page hook, the table — sees the
 * domain shapes in `@/api/models/TrialBalance`. Same seam, and the same reason,
 * as `accountsMapping`, `journalMapping`, `statementsMapping`, `vatMapping`,
 * `periodsMapping` and `fixedAssetsMapping`.
 *
 * Unusually for this codebase, the wire and the domain barely disagree: the
 * response was measured **before** the client was written, so there is no
 * inherited guess to reconcile. What this file is for instead is the four
 * properties of the response that a well-meaning consumer would otherwise
 * "improve":
 *
 * 1. **The columns are the server's.** Each row carries `debit` and `credit`
 *    outright, so the client never places a figure by looking at the sign of a
 *    balance. Measured: account 1220 *Accumulated depreciation* arrives as
 *    `type: ASSET` with `creditTetri: 1_500_000` and `debitTetri: 0` — a contra
 *    account on the credit side, typed as an asset, with no client-side rule
 *    involved. Exactly one column is non-zero on every row.
 * 2. **`balanced` is the server's verdict** and is carried through untouched.
 *    The server computes it as `totalDebitTetri === totalCreditTetri` over the
 *    same rows it sent. Recomputing it here would produce a second opinion about
 *    the one fact this report exists to state — and, worse, one that could
 *    silently disagree if a row were ever dropped in mapping.
 * 3. **The totals are the server's**, not a sum of the rows, for the same
 *    reason. See {@link mapTrialBalance} for what happens when a row cannot be
 *    read.
 * 4. **`net` and the decimal companions are dropped on purpose.** `netTetri` is
 *    the signed, debit-positive balance; `debitTetri` is `max(net, 0)` and
 *    `creditTetri` is `max(-net, 0)` (verified against the server's own
 *    implementation), so `net` carries no information the two columns do not.
 *    The `debit` / `credit` / `totalDebit` / `totalCredit` companions go the same
 *    way: every consumer renders through `formatGel` from the integer, which is
 *    the project's money contract, so carrying a second display path that
 *    nothing exercises only creates something that can drift. Note they arrive
 *    as **JSON numbers, not decimal strings** (`7000` for `700000` tetri) — the
 *    same trap as the statements' `value` — which is a second reason not to
 *    depend on them.
 *
 * One more fact about the wire, easy to misread:
 *
 * - **`asOf` is nullable.** It echoes the request, and the request may omit it
 *   (the server then covers all history). This client always sends one, and the
 *   screen writes its heading from this echo rather than from the date the user
 *   asked for — see `TrialBalance.asOf`.
 */

// ── wire shapes ─────────────────────────────────────────────────────────────

export type TrialBalanceRowWire = {
    accountId?: string;
    code?: string;
    name?: string;
    type?: string;
    /** JSON number, not a decimal string. Display echo only. */
    debit?: number | null;
    debitTetri?: number;
    credit?: number | null;
    creditTetri?: number;
    /** Signed, debit-positive. Deliberately not carried into the domain. */
    net?: number | null;
    netTetri?: number;
};

export type TrialBalanceWire = {
    items?: TrialBalanceRowWire[] | null;
    totalDebit?: number | null;
    totalDebitTetri?: number;
    totalCredit?: number | null;
    totalCreditTetri?: number;
    balanced?: boolean;
    asOf?: string | null;
};

// ── primitives ──────────────────────────────────────────────────────────────

const isFiniteInt = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);

const ACCOUNT_TYPES: readonly string[] = [
    'ASSET',
    'LIABILITY',
    'EQUITY',
    'REVENUE',
    'EXPENSE',
];

const accountTypeOf = (wire: string | undefined): AccountType | null =>
    typeof wire === 'string' && ACCOUNT_TYPES.includes(wire)
        ? (wire as AccountType)
        : null;

// ── rows ────────────────────────────────────────────────────────────────────

/**
 * `null` when the row cannot be read as an account with a placed balance — no
 * id, an unknown `type`, or a missing figure on either side.
 *
 * Unlike the VAT report, an unreadable row here is **not** dropped and carried
 * on from; see {@link mapTrialBalance}.
 */
export const mapTrialBalanceRow = (
    wire: TrialBalanceRowWire,
): TrialBalanceRow | null => {
    const type = accountTypeOf(wire.type);
    if (
        !wire.accountId ||
        !type ||
        !isFiniteInt(wire.debitTetri) ||
        !isFiniteInt(wire.creditTetri)
    ) {
        return null;
    }
    return {
        accountId: wire.accountId,
        code: wire.code ?? '',
        name: wire.name ?? wire.code ?? '',
        type,
        debitTetri: Math.trunc(wire.debitTetri),
        creditTetri: Math.trunc(wire.creditTetri),
    };
};

// ── the report ──────────────────────────────────────────────────────────────

/**
 * `null` when the body cannot be read as a trial balance — a missing `items`
 * array, missing totals, a missing verdict, **or a single unreadable row**.
 *
 * That last one is the difference from every other mapping in this folder, and
 * it is deliberate. Elsewhere an unmappable line is dropped and the read
 * continues, because a report missing one line is better than no report. Here it
 * is the opposite: the totals and the `balanced` verdict are the server's, over
 * the rows the server sent. Drop a row and the screen shows a list that does not
 * add up to the total printed beneath it, under a green "In balance" chip — a
 * trial balance that is wrong in precisely the way the screen exists to detect.
 * The page's error-with-retry state is the correct outcome instead.
 */
export const mapTrialBalance = (
    wire: TrialBalanceWire,
): TrialBalance | null => {
    if (
        !Array.isArray(wire.items) ||
        !isFiniteInt(wire.totalDebitTetri) ||
        !isFiniteInt(wire.totalCreditTetri) ||
        typeof wire.balanced !== 'boolean'
    ) {
        return null;
    }

    const rows: TrialBalanceRow[] = [];
    for (const item of wire.items) {
        const row = mapTrialBalanceRow(item);
        if (!row) {
            return null;
        }
        rows.push(row);
    }

    return {
        asOf: wire.asOf ?? null,
        // The server orders by account code; that order is carried, not redone.
        rows,
        totalDebitTetri: Math.trunc(wire.totalDebitTetri),
        totalCreditTetri: Math.trunc(wire.totalCreditTetri),
        balanced: wire.balanced,
    };
};
